import { useCallback, useRef, useState } from 'react';
import type { AppEdge, AppNode } from '../types';

export type FlowHistorySnapshot = {
  nodes: AppNode[];
  edges: AppEdge[];
};

const MAX_HISTORY_ENTRIES = 50;
const RUNTIME_KEYS = new Set(['selected', 'dragging', 'className', 'measured', 'resizing']);

const normalize = <T,>(value: T): T => JSON.parse(JSON.stringify(value, (key, entry) => (
  RUNTIME_KEYS.has(key) ? undefined : entry
))) as T;

export const createFlowHistorySnapshot = (
  nodes: AppNode[],
  edges: AppEdge[],
): FlowHistorySnapshot => normalize({ nodes, edges });

const snapshotKey = (snapshot: FlowHistorySnapshot) => JSON.stringify(snapshot);

export const useFlowHistory = (initialSnapshot: FlowHistorySnapshot) => {
  const presentRef = useRef(normalize(initialSnapshot));
  const pastRef = useRef<FlowHistorySnapshot[]>([]);
  const futureRef = useRef<FlowHistorySnapshot[]>([]);
  const [, setRevision] = useState(0);

  const refresh = useCallback(() => setRevision((revision) => revision + 1), []);

  const commit = useCallback((snapshot: FlowHistorySnapshot) => {
    const normalized = normalize(snapshot);
    if (snapshotKey(normalized) === snapshotKey(presentRef.current)) return;
    pastRef.current = [...pastRef.current, presentRef.current].slice(-MAX_HISTORY_ENTRIES);
    presentRef.current = normalized;
    futureRef.current = [];
    refresh();
  }, [refresh]);

  const undo = useCallback((): FlowHistorySnapshot | null => {
    const previous = pastRef.current.at(-1);
    if (!previous) return null;
    pastRef.current = pastRef.current.slice(0, -1);
    futureRef.current = [presentRef.current, ...futureRef.current].slice(0, MAX_HISTORY_ENTRIES);
    presentRef.current = previous;
    refresh();
    return normalize(previous);
  }, [refresh]);

  const redo = useCallback((): FlowHistorySnapshot | null => {
    const next = futureRef.current[0];
    if (!next) return null;
    futureRef.current = futureRef.current.slice(1);
    pastRef.current = [...pastRef.current, presentRef.current].slice(-MAX_HISTORY_ENTRIES);
    presentRef.current = next;
    refresh();
    return normalize(next);
  }, [refresh]);

  const reset = useCallback((snapshot: FlowHistorySnapshot) => {
    presentRef.current = normalize(snapshot);
    pastRef.current = [];
    futureRef.current = [];
    refresh();
  }, [refresh]);

  return {
    canUndo: pastRef.current.length > 0,
    canRedo: futureRef.current.length > 0,
    commit,
    undo,
    redo,
    reset,
  };
};